"use client"

import type React from "react"

import { Box, TextField, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material"
import type { MigrationFormData } from "../../../types/migrations"
import { FileUploader } from "./FileUploader"

interface SetupFormProps {
  formData: MigrationFormData
  formErrors: Partial<MigrationFormData>
  formSubmitted: boolean
  file: File | null
  fileUploadStatus: "idle" | "uploading" | "success" | "error"
  uploadProgress: number
  onChange: (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => void
  onFileUpload: (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => void
}

/**
 * Component for the initial migration setup form
 */
export const SetupForm = ({
  formData,
  formErrors,
  formSubmitted,
  file,
  fileUploadStatus,
  uploadProgress,
  onChange,
  onFileUpload,
  onRemoveFile,
}: SetupFormProps) => {
  return (
    <>
      <Typography variant="h6" sx={{ mb: 3, color: "#414141" }}>
        Initial Setup
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          name="projectName"
          label="Migration name"
          placeholder="Enter a name"
          value={formData.projectName}
          onChange={onChange}
          fullWidth
          required
          error={formSubmitted && !!formErrors.projectName}
          helperText={formSubmitted && formErrors.projectName}
          inputProps={{ maxLength: 50 }}
        />
        <FormControl fullWidth required error={formSubmitted && !!formErrors.llmModel}>
          <InputLabel id="llm-select-label">LLM</InputLabel>
          <Select labelId="llm-select-label" name="llmModel" value={formData.llmModel} label="LLM" onChange={onChange}>
            <MenuItem value="">
              <em>Select an LLM</em>
            </MenuItem>
            <MenuItem value="gemini">Gemini</MenuItem>
            <MenuItem value="openai">OpenAi</MenuItem>
          </Select>
          {formSubmitted && formErrors.llmModel && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.llmModel}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Typography sx={{ mb: 2, color: "#666666" }}>
        For the Migration, select first the source system, and then the destination system.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 4 }}>
        <FormControl fullWidth required error={formSubmitted && !!formErrors.sourceSystem}>
          <InputLabel>Source System</InputLabel>
          <Select name="sourceSystem" value={formData.sourceSystem} label="Source System" onChange={onChange}>
            <MenuItem value="ibm">IBM API Connect</MenuItem>
          </Select>
          {formSubmitted && formErrors.sourceSystem && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.sourceSystem}
            </Typography>
          )}
        </FormControl>
        <Box sx={{ px: 2 }}>→</Box>
        <FormControl fullWidth required error={formSubmitted && !!formErrors.destinationSystem}>
          <InputLabel>Destination System</InputLabel>
          <Select
            name="destinationSystem"
            value={formData.destinationSystem}
            label="Destination System"
            onChange={onChange}
          >
            <MenuItem value="APIGEE">Apigee X</MenuItem>
          </Select>
          {formSubmitted && formErrors.destinationSystem && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {formErrors.destinationSystem}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Typography sx={{ mb: 2, color: "#666666" }}>
        Select the file that will be used <span style={{ color: "red" }}>*</span>
      </Typography>
      <FileUploader
        file={file}
        fileUploadStatus={fileUploadStatus}
        uploadProgress={uploadProgress}
        formSubmitted={formSubmitted}
        onFileUpload={onFileUpload}
        onRemoveFile={onRemoveFile}
      />
    </>
  )
}
